import React, { useEffect, useRef } from "react";
import { Scrollbars } from "react-custom-scrollbars-4";
import { useSnackbar } from "notistack";
import {
  Link,
  CircularProgress,
  Backdrop,
  Container,
  Stack,
  Breadcrumbs,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  List,
  Box,
  Collapse,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { getSchema } from "../helpers/schemas/getSchema";
import { checkInvalidField } from "../helpers/checkInvalidField";
import { getFormInvalid } from "../helpers/getFormInvalid";
import { saveToFirestore } from "../helpers/saveToFirestore";
import ImportDialog from "../Dialogs/ImportDialog";
import EditDialog from "../Dialogs/EditDialog";
import { getExcelFeatures } from "../helpers/getExcelFeatures";
import AddBoxIcon from "@mui/icons-material/AddBox";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AssetCard from "./AssetCard";
import { getEquipmentSchema } from "../helpers/schemas/getEquipmentSchema";
import { VisuallyHiddenInput } from "../helpers/VisuallyHiddenInput";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { getCounts } from "../helpers/getCounts";
import { Unpublished } from "@mui/icons-material";
import { getUserAccess } from "../helpers/getUserAccess";
import SortDialog from "../Dialogs/SortDialog";
import { applyEditTrackingFields } from "../helpers/applyEditTrackingFields";
import { getSortArray } from "../helpers/getSortArray";
import { onEditSaveAll } from "../helpers/onEditSaveAll";
import { getProject } from "../helpers/getProject";
import { processFiles } from "../helpers/processFiles";
import { getEditorTrackingFieldNames } from "../helpers/schemas/getEditorTrackingFields";

function Assets(props) {
  const fileInputRef = useRef(null);
  const fileInputRef1 = useRef(null);
  const navigate = useNavigate();
  // const location = useLocation();
  // const { passProject } = location.state || {}; // Destructure and provide a default empty object
  // console.log(location.state);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);
  const [importDialogOpen, setImportDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);

  const [project, setProject] = React.useState({});
  const [allEquipment, setAllEquipment] = React.useState([]);
  const [equipment, setEquipment] = React.useState([]);
  const [sortByValue, setSortByValue] = React.useState("Name");
  const [sortByOptions] = React.useState([
    "Name",
    "Name Desc",
    "Type",
    "Type Desc",
    "Discipline",
    "Discipline Desc",
    "System",
    "System Desc",
    "Space",
    "Space Desc",
  ]);
  const [equipmentSchema, setEquipmentSchema] = React.useState([]);
  const [importTitle, setImportTitle] = React.useState(
    "Equipment Import > Choose Worksheet",
  );
  const [importStats, setImportStats] = React.useState([]);
  const [importSubtitle, setImportSubtitle] = React.useState(
    `Choose the worksheet with the data you wish to import.`,
  );
  const [importData, setImportData] = React.useState([]);
  const [importFields, setImportFields] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [filterValue, setFilterValue] = React.useState("");
  const [editFeature, setEditFeature] = React.useState(null);
  const [editSchema, setEditSchema] = React.useState([]);
  const [formErrorMessage, setformErrorMessage] = React.useState("");
  const [sheetName, setSheetName] = React.useState(null);
  const [workbook, setWorkBook] = React.useState({ SheetNames: [] });
  const [editFiles, setEditFiles] = React.useState([]);

  useEffect(() => {
    // 1. Define the async function inside the effect
    async function fetchData() {
      try {
        const params = window.location.href.split("/");

        const proj = await getProject(params[params.length - 2]);

        setIsLoading(false);
        setProject(proj);
        setEquipmentSchema(getSchema("equipment"));
        var equips = [...proj.Equipment];
        equips = getCounts(
          equips,
          proj.Files,
          proj.Issues,
          proj.Comments,
          "equipment_id",
          proj.Checklists,
        );
        setAllEquipment(equips);
        setEditSchema(getSchema("equipment"));
        onSortChange(sortByValue, equips);
      } catch (error) {
        console.error("Error getting document:", error);
        enqueueSnackbar(error.message, {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          variant: "error",
        });
      } finally {
        setIsLoading(false);
      }
    }

    if (isLoading === true && isReady === false) {
      setIsReady(true);
      fetchData();
    }
  });

  const onAddEquipment = () => {
    var item = {};
    var schema = getSchema("equipment");
    schema.forEach((fd) => {
      item[fd.name] = "";
    });

    item["id"] = "new";
    item = applyEditTrackingFields(item, props.user);

    setEditSchema(schema);
    setEditFeature(item);
    setEditDialogOpen(true);
  };

  const onExportEquipment = () => {
    try {
      const dataToExport = equipment.map(({ Selected, ...rest }) => rest);
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Equipment");
      XLSX.writeFile(workbook, `${project.Name}_Equipment.xlsx`);
    } catch (error) {
      console.error("Error exporting equipment:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onEdit = (id) => {
    const item = equipment.find((f) => f.id === id);
    var schema = getSchema("equipment");
    const schemaFields = schema.map((f) => f.name);

    for (const key in item) {
      if (key !== "Selected" && !schemaFields.includes(key)) {
        schema.push({
          name: key,
          default: "",
          type: typeof item[key],
          maxLength:
            item[key].toString().length < 255
              ? 255
              : item[key].toString().length,
          selectables: [],
          similair: [],
          helperText: "",
          nullable: true,
          readOnly: false,
          error: false,
        });
      }
    }
    setEditSchema(schema);
    setEditFeature({ ...item });
    const files = project.Files.filter((p) => p.equipment_id === id);
    setEditFiles([...files]);
    setEditDialogOpen(true);
  };

  const onEditDelete = async () => {
    try {
      const updatedArray1 = project.Equipment.filter(
        (fd) => fd.id !== editFeature.id,
      );
      project.Equipment = updatedArray1;

      project.Checklists = project.Checklists.filter(
        (item) =>
          item.hasOwnProperty("equipment_id") === false ||
          (item.hasOwnProperty("equipment_id") &&
            item.equipment_id !== editFeature.id),
      );

      project.Comments = project.Comments.filter(
        (item) =>
          item.hasOwnProperty("equipment_id") === false ||
          (item.hasOwnProperty("equipment_id") &&
            item.equipment_id !== editFeature.id),
      );

      project.Files = project.Files.filter(
        (item) =>
          item.hasOwnProperty("equipment_id") === false ||
          (item.hasOwnProperty("equipment_id") &&
            item.equipment_id !== editFeature.id),
      );

      project.Issues = project.Issues.filter(
        (item) =>
          item.hasOwnProperty("equipment_id") === false ||
          (item.hasOwnProperty("equipment_id") &&
            item.equipment_id !== editFeature.id),
      );

      await saveToFirestore("projects", project);

      const updatedArray2 = allEquipment.filter(
        (fd) => fd.id !== editFeature.id,
      );
      setAllEquipment([...updatedArray2]);

      const updatedArray3 = equipment.filter((fd) => fd.id !== editFeature.id);
      onSortChange(sortByValue, [...updatedArray3]);

      // setPage(newPage);
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    } finally {
      enqueueSnackbar("Equipment deleted successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
    }
  };

  const onEditFileCommentChange = (e) => {
    var file = editFiles.find((f) => f.id === e.target.id);
    file.comment = e.target.value;
    setEditFiles([...editFiles]);
  };

  const onRemoveEditFile = async (id) => {
    const updatedFiles = editFiles.map((obj) => {
      if (obj.id === id) {
        obj.deleted = true;
        return obj; // Replace the object if its ID matches
      }
      return obj; // Keep other objects as they are
    });

    setEditFiles([...updatedFiles]);
  };

  const getAcceptedFiles = async (files) => {
    try {
      const readyFiles = await processFiles(
        files,
        "equipment_id",
        editFeature.id,
      );
      const allFiles = editFiles.concat(readyFiles);
      setEditFiles([...allFiles]);
    } catch (error) {
      console.error("Error getting document:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        variant: "error",
      });
    }
  };

  const editSave = async () => {
    try {
      //check for invalid
      var feat = { ...editFeature };
      var invalidResults = getFormInvalid(feat, editSchema);
      if (invalidResults.isInvalid === true) {
        setEditFeature({ ...invalidResults.feature });
        setEditSchema([...invalidResults.schema]);
        setformErrorMessage(invalidResults.formErrorMessage);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const savedProj = await onEditSaveAll(
        project,
        feat,
        editSchema,
        editFiles,
        "Equipment",
        "equipment_id",
        props.user,
      );

      props.saveCurrentProject({ ...savedProj });
      setEditDialogOpen(false);
      setProject({ ...savedProj });
      setAllEquipment([...savedProj.Equipment]);
      onSortChange(sortByValue, [...savedProj.Equipment]);
      console.log(savedProj.Equipment);
      enqueueSnackbar("Equipment saved successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onEditChange = (e) => {
    try {
      // console.log(e);
      var att = editSchema.find((item) => item.name === e.target.name);
      //clearout erro message field
      const formErrorMessage = checkInvalidField(e.target.value, att);
      //format dates
      if (att.type === "date") {
        if (e.target.value.length > 0) {
          var date = new Date(e.target.value);
          // date.setDate(date.getDate() + 1);
          editFeature[e.target.name] = date.toISOString();
          // editFeature[e.target.name] = date.getTime();
          setEditFeature({ ...editFeature });
          setformErrorMessage(formErrorMessage);
        }
      } else if (att.type === "bit") {
        editFeature[e.target.name] = e.target.checked;
        setEditFeature({ ...editFeature });
        setformErrorMessage(formErrorMessage);
      } else {
        editFeature[e.target.name] = e.target.value;
        setEditFeature({ ...editFeature });
        setformErrorMessage(formErrorMessage);
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onEditCancel = () => {
    setEditDialogOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    // console.log(newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function paginateArray(array) {
    // Adjust pageNumber for 0-based indexing in array.slice
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return array.slice(startIndex, endIndex);
  }

  const onSortChange = (value, array) => {
    if (value !== sortByValue) {
      setSortByValue(value);
    }
    const obs = getSortArray(value, array);
    setEquipment([...obs]);
  };

  const onFilterChange = (value) => {
    const searchTerm = value;
    if (searchTerm.length > 2) {
      const filteredData = allEquipment.filter((obj) => {
        // Get an array of all property values for the current object
        const values = Object.values(obj);

        // Check if any of these values (converted to string and lowercase) include the searchTerm
        return values.some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        );
      });

      setEquipment([...filteredData]);
    } else {
      setEquipment([...allEquipment]);
    }
    setFilterValue(searchTerm);
    setPage(0);
  };

  const handleChecked = (e) => {
    equipment[Number(e.target.id)].Selected =
      !equipment[Number(e.target.id)].Selected;
    setEquipment([...equipment]);
  };

  const onCloseImportDialog = () => {
    setImportDialogOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    if (fileInputRef1.current) {
      fileInputRef1.current.value = null;
    }
    setSheetName(null);
    setImportTitle("Equipment Import > Choose Worksheet");
    setImportSubtitle(`Choose the worksheet with the data you wish to import.`);
  };

  const onAttributeChange = (e) => {
    var fd = importFields.find((f) => f.name === e.target.name);
    fd.attribute = e.target.value;
    setImportFields([...importFields]);
  };

  const onFldChange = (e) => {
    var fd = importFields.find((f) => f.name === e.target.name);
    fd.selected = e.target.value;
    setImportFields([...importFields]);
  };

  const processExcel = (file) => {
    try {
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          setWorkBook(workbook);
          setImportDialogOpen(true);
        };
        reader.readAsArrayBuffer(file);
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onWorksheetSelected = async (e) => {
    try {
      // Assuming you want to read the first sheet
      const worksheet = workbook.Sheets[e];

      // Convert sheet data to an array of objects or an array of arrays
      const jsonData = XLSX.utils.sheet_to_json(worksheet); // Array of objects
      // const arrayOfArrays = XLSX.utils.sheet_to_array(worksheet); // Array of arrays

      console.log(jsonData); // Or update your component state with this data
      if (jsonData.length > 0) {
        var fields = [];
        Object.keys(jsonData[0]).forEach((colName) => {
          var match = equipmentSchema.find((f) => f.name === colName);
          if (match === undefined) {
            equipmentSchema.forEach((fd) => {
              if (
                fd.hasOwnProperty("similair") &&
                fd.similair.includes(colName.toLowerCase())
              )
                match = fd;
            });
          }

          var availableFds = equipmentSchema
            .filter(
              (f) =>
                f.name !== "id" &&
                getEditorTrackingFieldNames().includes(f.name) === false &&
                f.name !== "fileCount" &&
                f.name !== "commentCount" &&
                f.name !== "No",
            )
            .map((f) => f.name);

          availableFds.push("New Attribute");

          fields.push({
            name: colName,
            selected: match ? match.name : "New Attribute",
            options: availableFds,
            attribute: colName,
            error: false,
          });
        });
        setImportData(jsonData);
        setSheetName(e);
        setImportFields(fields);
        setImportTitle("Equipment Import > Choose Fields");
        setImportSubtitle(
          `For each column of your spreadsheet, select the corresponding field. The "Name" field is required.`,
        );
      } else {
        throw new Error("Unable to import equipment");
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onImportExcel = async () => {
    try {
      if (importData.length === 0) {
        onCloseImportDialog();
        return;
      }

      const results = await getExcelFeatures(
        props.user,
        importData,
        importFields,
        editSchema,
        getEquipmentSchema()
          .filter((fd) => fd.name !== "id" && fd.nullable === false)
          .map((fd) => fd.name),
        project,
        "Equipment",
        null,
        null,
      );

      const statss = getStats(results.newEquip);
      setImportStats([...statss]);
      setImportSubtitle("Import Successful: Results shown below");
      setImportTitle("Equipment Import > Complete");
      setImportFields([]);
      var equ = [...results.newEquip];
      equ.forEach((item) => {
        item.Selected = false;
      });
      setFilterValue("");
      setPage(0);
      setProject({ ...results.project });
      console.log(equ);
      onSortChange(sortByValue, equ);
      setAllEquipment(equ);
      setImportData([]);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const getStats = (array) => {
    var importStat = [];
    importStat.push({ name: "Equipment", count: array.length });
    importStat.push({
      name: "Types",
      count: new Set(array.map((item) => item.Types)).size,
    });
    importStat.push({
      name: "Spaces",
      count: new Set(array.map((item) => item.Space)).size,
    });
    importStat.push({
      name: "Systems",
      count: new Set(array.map((item) => item.System)).size,
    });
    return importStat;
  };

  const onUncheckSelected = () => {
    equipment.forEach((item) => {
      if (item.Selected === true) {
        item.Selected = false;
      }
    });
    setEquipment([...equipment]);
  };

  // const onDeleteSelected = async () => {
  //   try {
  //     setIsLoading(true);
  //     const deleteIds = equipment
  //       .filter((item) => item.Selected === true)
  //       .map((i) => i.id);

  //     //set remaining equipment
  //     project.Equipment = project.Equipment.filter(
  //       (item) => !deleteIds.includes(item.id),
  //     );

  //     //set remaining Checklists, Files, Issues, Comments
  //     project.Checklists = project.Checklists.filter(
  //       (item) =>
  //         item.hasOwnProperty("equipment_id") === false ||
  //         (item.hasOwnProperty("equipment_id") &&
  //           !deleteIds.includes(item.equipment_id)),
  //     );

  //     project.Files = project.Files.filter(
  //       (item) =>
  //         item.hasOwnProperty("equipment_id") === false ||
  //         (item.hasOwnProperty("equipment_id") &&
  //           !deleteIds.includes(item.equipment_id)),
  //     );

  //     project.Issues = project.Issues.filter(
  //       (item) =>
  //         item.hasOwnProperty("equipment_id") === false ||
  //         (item.hasOwnProperty("equipment_id") &&
  //           !deleteIds.includes(item.equipment_id)),
  //     );

  //     project.Comments = project.Comments.filter(
  //       (item) =>
  //         item.hasOwnProperty("equipment_id") === false ||
  //         (item.hasOwnProperty("equipment_id") &&
  //           !deleteIds.includes(item.equipment_id)),
  //     );
  //     const savedProj = { ...project };

  //     await saveToFirestore("projects", savedProj);
  //     setProject({ ...savedProj });
  //     setAllEquipment([...savedProj.Equipment]);
  //     onSortChange(sortByValue, savedProj.Equipment);
  //     enqueueSnackbar("Selected assets deleted successfully", {
  //       anchorOrigin: {
  //         vertical: "bottom",
  //         horizontal: "center",
  //       },
  //       variant: "success",
  //     });
  //     setIsLoading(false);
  //   } catch (error) {
  //     console.error("Error:", error);
  //     enqueueSnackbar(error.message, {
  //       anchorOrigin: {
  //         vertical: "bottom",
  //         horizontal: "center",
  //       },
  //       variant: "error",
  //     });
  //     setIsLoading(false);
  //   }
  // };

  const onDrawComplete = (id, pic) => {
    var file = editFiles.find((f) => f.id === id);
    if (file) {
      file.file = pic;
      if (!file.id.startsWith("new_")) {
        file.isDrawn = true;
      }
      setEditFiles([...editFiles]);
    }
  };

  return isLoading ? (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={isLoading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : (
    <Scrollbars
      autoHide
      autoHideTimeout={10}
      style={{ width: "100%", height: "100%" }}
    >
      <Toolbar />
      <Container
        maxWidth="xl"
        style={{
          // paddingTop: 30,
          paddingBottom: 30,
          // paddingBottom: theme.spacing(4),
          justifyContent: "center",
        }}
      >
        <Typography
          color="primary.light"
          style={{ paddingTop: 40 }}
          variant="h5"
          gutterBottom
        >
          Assets
        </Typography>

        <Stack
          sx={{
            justifyContent: "center",
            alignItems: "center",
          }}
          spacing={2}
        >
          <Breadcrumbs separator="›" aria-label="breadcrumb">
            <Link
              underline="hover"
              key="1"
              color="inherit"
              href=""
              onClick={() =>
                navigate(`/project/${project.id}/dashboard`, {
                  state: { passProject: project },
                })
              }
            >
              Project: {project.Name}
            </Link>
            ,
            <Typography
              underline="hover"
              key="2"
              color="inherit"
              href={`/project/${project.id}/equipment`}

              // onClick={handleClick}
            >
              {`Assets (${allEquipment.length})`}
            </Typography>
            ,
          </Breadcrumbs>
        </Stack>

        <Stack
          // style={{ paddingTop: 10 }}
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <Tooltip title="Upload Assets">
            <IconButton
              color="primary"
              component="label"
              disabled={
                !getUserAccess(props.user, project.uid, project.Contacts)
                  .allowCreate
              }
              role={undefined}
              variant="contained"
              tabIndex={-1}
            >
              <VisuallyHiddenInput
                type="file"
                ref={fileInputRef}
                onChange={(event) =>
                  event.target.files.length > 0 &&
                  processExcel(event.target.files[0])
                }
              />
              <FileUploadIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Assets">
            <IconButton
              onClick={onAddEquipment}
              aria-label="Add Assets"
              size="large"
              color="primary"
              disabled={
                !getUserAccess(props.user, project.uid, project.Contacts)
                  .allowCreate
              }
            >
              <AddBoxIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Assets">
            <IconButton
              onClick={onExportEquipment}
              aria-label="Export Assets"
              disabled={equipment.length === 0}
              size="large"
              color="primary"
            >
              <CloudDownloadIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack
          style={{ paddingTop: 30 }}
          sx={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {allEquipment.length > 0 && (
            <SortDialog
              sortByValue={sortByValue}
              onSortChange={onSortChange}
              objArray={equipment}
              sortByOptions={sortByOptions}
              filterValue={filterValue}
              onFilterChange={onFilterChange}
              page={page}
              handleChangePage={handleChangePage}
              rowsPerPage={rowsPerPage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          )}

          <Collapse
            style={{ width: "100%" }}
            in={equipment.some((item) => item.Selected === true)}
          >
            <Paper elevation={6} style={{ width: "100%" }}>
              <Box
                sx={{
                  width: "100%",
                  height: 60,
                  borderRadius: 1,
                  bgcolor: "secondary.light",
                  // "&:hover": {
                  //   bgcolor: "primary.dark",
                  // },
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                  style={{ height: "100%", paddingLeft: 16 }}
                >
                  <Typography color="primary.contrastText">
                    {`Selected ${
                      equipment.filter((f) => f.Selected === true).length
                    }`}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onUncheckSelected}
                    startIcon={<Unpublished />}
                  >
                    Uncheck All
                  </Button>

                  {/* <Button
                    variant="contained"
                    color="secondary"
                    disabled={
                      !getUserAccess(props.user, project.uid, project.Contacts)
                        .allowDelete
                    }
                    startIcon={<Delete />}
                    onClick={onDeleteSelected}
                  >
                    {`Delete Selected`}
                  </Button> */}
                </Stack>
              </Box>
            </Paper>
          </Collapse>

          {equipment.length === 0 ? (
            <Typography>
              There isn't any equipment associated with this project. Please
              <Tooltip title="Upload Assets">
                <IconButton
                  color="primary"
                  component="label"
                  role={undefined}
                  variant="contained"
                  tabIndex={-1}
                  size="medium"
                >
                  <VisuallyHiddenInput
                    type="file"
                    ref={fileInputRef1}
                    onChange={(event) =>
                      event.target.files.length > 0 &&
                      processExcel(event.target.files[0])
                    }
                  />
                  <FileUploadIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              upload or add
              <Tooltip title="Add Assets">
                <IconButton
                  onClick={onAddEquipment}
                  aria-label="Add Assets"
                  size="medium"
                  color="primary"
                >
                  <AddBoxIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              new equipment
            </Typography>
          ) : (
            <List style={{ width: "100%" }}>
              {paginateArray(equipment).map((e, idx) => (
                <AssetCard
                  e={e}
                  idx={idx}
                  onEdit={onEdit}
                  handleChecked={handleChecked}
                  key={idx}
                  projectID={project.id}
                  allowUpdate={
                    getUserAccess(props.user, project.uid, project.Contacts)
                      .allowUpdate
                  }
                />
              ))}
            </List>
          )}
        </Stack>
        <ImportDialog
          isOpen={importDialogOpen}
          onClose={onCloseImportDialog}
          importTitle={importTitle}
          importSubtitle={importSubtitle}
          importData={importData}
          importFields={importFields}
          onFldChange={onFldChange}
          onAttributeChange={onAttributeChange}
          onImportExcel={onImportExcel}
          importStats={importStats}
          sheetName={sheetName}
          workbook={workbook}
          onWorksheetSelected={onWorksheetSelected}
        />
        <EditDialog
          allowDeleteAccess={
            getUserAccess(props.user, project.uid, project.Contacts).allowDelete
          }
          feature={editFeature}
          editFiles={editFiles}
          importTitle={importTitle}
          getAcceptedFiles={getAcceptedFiles}
          onEditFileCommentChange={onEditFileCommentChange}
          onRemoveEditFile={onRemoveEditFile}
          schema={editSchema}
          onChange={onEditChange}
          onEditSave={editSave}
          onEditDelete={onEditDelete}
          isOpen={editDialogOpen}
          onClose={onEditCancel}
          formErrorMessage={formErrorMessage}
          allowDelete={
            getUserAccess(props.user, project.uid, project.Contacts).allowDelete
          }
          allowFiles={true}
          onDrawComplete={onDrawComplete}
        />
      </Container>
    </Scrollbars>
  );
}

export default Assets;
